import * as React from 'react';
import { useFormikContext } from 'formik';
import { addHours, format, startOfDay, subMonths } from 'date-fns';
import { CloseRounded } from '@mui/icons-material';
import { Button } from '@mui/material';
import { LoadedUser, Option } from '../../interfaces';
import { getArtistOptionsForService } from '../../services/business';
import {
  getDefaultProviderCommissionType,
  getEmployeeServiceCommissionRate,
  getProviderCommissionOptions,
} from '../../helpers/payments';
import {
  clearCheckoutAppointment,
  isCheckoutProviderChangeLocked,
} from '../../helpers/appointments';
import useUser from '../../hooks/global/useUser';
import {
  CheckoutFormValues,
  SelectedAppointment,
} from '../../types/appointments';
import InputRow from './InputRow';
import useGetAppointments from '../../hooks/appointments/useGetAppointments';
import { getCustomerNameFromAppointment } from '../../utils/global/helper';
import SelectInput from './SelectInput';
import useConfirm from '../../hooks/useConfirm';

interface Props {
  selectedAppointment: SelectedAppointment | null;
  appointment: SelectedAppointment | null;
  onSelectAppointment?: (appointment: SelectedAppointment | null) => void;
  hideCommission?: boolean;
}

function ClearButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      type="button"
      onClick={onClick}
      sx={{ minWidth: 0, padding: 1, my: 'auto' }}>
      <CloseRounded
        style={{
          color: 'var(--darkGrey)',
          fontSize: 20,
        }}
      />
    </Button>
  );
}

export default function CheckoutDetails({
  selectedAppointment,
  appointment,
  onSelectAppointment,
  hideCommission,
}: Props) {
  const confirm = useConfirm();
  const { values, setFieldValue } = useFormikContext<CheckoutFormValues>();
  const { user: loadedUser } = useUser<LoadedUser>();

  const startDate = React.useMemo(
    () => format(startOfDay(subMonths(new Date(), 1)), 'yyyy-MM-dd'),
    [],
  );
  const endDate = React.useMemo(
    () => addHours(new Date(), 1).toISOString(),
    [],
  );
  const isSinglePassedAppointment = appointment && !onSelectAppointment;
  const { appointments } = useGetAppointments({
    dataStartDate: startDate,
    dataEndDate: endDate,
    isReversed: true,
    limit: 50,
    includeCanceled: false,
    onlyIncomplete: true,
    useExactDates: true,
    customerId: values.customer.customerId,
    enabled: !selectedAppointment && !isSinglePassedAppointment,
  });
  const appointmentsList = selectedAppointment
    ? [selectedAppointment]
    : isSinglePassedAppointment
    ? [appointment]
    : appointments;

  const etsRate = React.useMemo(
    () =>
      getEmployeeServiceCommissionRate(
        loadedUser.business.employees,
        loadedUser.business.services,
        values.employeeId,
        appointment?.serviceId,
      ),
    [
      loadedUser.business.services,
      loadedUser.business.employees,
      appointment?.serviceId,
      values.employeeId,
    ],
  );

  const commissionOptions: Option[] = React.useMemo(
    () =>
      getProviderCommissionOptions(
        loadedUser.business.employees,
        loadedUser.business.services,
        values.employeeId,
        appointment?.serviceId,
      ).map(o => ({
        label: `${(o.rate * 100).toFixed(0)}%`,
        value: o.type,
      })),
    [
      loadedUser.business.employees,
      loadedUser.business.services,
      values.employeeId,
      appointment?.serviceId,
    ],
  );

  React.useEffect(() => {
    if (!appointment) return;
    setFieldValue(
      'providerCommissionType',
      getDefaultProviderCommissionType(etsRate),
    );
  }, [values.employeeId, appointment, etsRate, setFieldValue]);

  const artistOptions = React.useMemo(
    () =>
      getArtistOptionsForService({
        serviceId: values.serviceId ? values.serviceId.toString() : undefined,
        user: loadedUser,
      }),
    [loadedUser, values.serviceId],
  );

  const onChangeAppt = async (_: string, value: string) => {
    if (!onSelectAppointment) return;

    const appt = appointments?.find(a => a.appointmentId === value);
    if (!appt) return;

    const itemsFromLibrary = (values.customOrderItems ?? []).filter(
      item => !!item.itemId,
    );
    if (!appt.businessId && itemsFromLibrary.length) {
      const confirmed = await confirm({
        title: 'Incompatible Items',
        message:
          'Some selected items are not compatible with this appointment. They will be removed if you continue.',
        negativeText: 'Cancel',
        positiveText: 'Continue',
      });
      if (!confirmed) return;
      setFieldValue(
        'customOrderItems',
        (values.customOrderItems ?? []).filter(item => !item.itemId),
      );
    }

    setFieldValue('appointmentId', value);
    setFieldValue('employeeId', appt.employeeId);
    setFieldValue('price', appt.price || 0);
    setFieldValue('customer.firstName', appt.customer.firstName);
    setFieldValue('customer.lastName', appt.customer.lastName);
    setFieldValue('customer.phoneNumber', appt.customer.phoneNumber);
    setFieldValue('customer.email', appt.customer.email);
    setFieldValue('customer.dob', appt.customer.dob);
    setFieldValue('customer.customerId', appt.customerId);
    setFieldValue('customer.role', appt.customer.role);

    onSelectAppointment(appt);
  };

  const onClearAppt = () => {
    if (!onSelectAppointment) return;
    clearCheckoutAppointment({
      paymentMethod: values.paymentMethod,
      setFieldValue,
      onSelectAppointment,
    });
  };

  const onClearEmployee = () => {
    setFieldValue('employeeId', '');
  };

  const disableEmployeeChange = isCheckoutProviderChangeLocked(
    appointment,
    values.employeeId,
    loadedUser,
  );

  return (
    <div className="input-group appointment-details">
      <div className="inputs">
        <div className="input-row">
          <SelectInput
            label="Appointment"
            name="appointmentId"
            options={(appointmentsList ?? []).map(appt => ({
              label: `${getCustomerNameFromAppointment(appt)}${
                appt.startDateTime
                  ? ` • ${format(
                      new Date(appt.startDateTime!),
                      'MMMM do, h:mma',
                    )}`
                  : appt.status === 'QUEUED'
                  ? ' • Walk-in'
                  : ''
              }`,
              value: appt.appointmentId,
            }))}
            placeholder="Select an appointment"
            autoWidth={false}
            disabled={!!selectedAppointment || !onSelectAppointment}
            values={values}
            setFieldValue={onChangeAppt}
            renderSuffix={
              appointment && !selectedAppointment && onSelectAppointment
                ? () => <ClearButton onClick={onClearAppt} />
                : undefined
            }
          />
        </div>
        {artistOptions && (
          <div className="input-row">
            <SelectInput
              options={artistOptions}
              name="employeeId"
              label={`Provider${
                appointment && !appointment.employeeId ? ' *' : ''
              }`}
              placeholder="Select a provider"
              disabled={disableEmployeeChange}
              autoWidth={false}
              values={values}
              setFieldValue={setFieldValue}
              renderSuffix={
                !appointment && values.employeeId && !disableEmployeeChange
                  ? () => <ClearButton onClick={onClearEmployee} />
                  : undefined
              }
              isError={!!appointment && !values.employeeId}
            />
          </div>
        )}
        {!hideCommission &&
          commissionOptions.length > 1 &&
          appointment &&
          !values.additionalServices?.length && (
            <InputRow
              formName="checkout"
              inputs={[
                {
                  type: 'select',
                  name: 'providerCommissionType',
                  label: 'Provider Commission',
                  options: commissionOptions,
                  placeholder: 'Select the artist commission',
                  disabled: false,
                  autoWidth: false,
                },
              ]}
              values={values}
              setFieldValue={setFieldValue}
            />
          )}
      </div>
    </div>
  );
}
